import { IdxStep, AuthenticatorKey } from '@mgmresorts/mgm-auth-js-sdk';
import { TextInput, TextLink, Alert, Icon } from '@mgmresorts/mgm-ui';
import { navigate } from 'hookrouter';
import React, { useContext, useState } from 'react';

import { useAppRedirect } from '../../../hooks/useAppRedirect';
import { useContent } from '../../../hooks/useContent';
import { usePasskeyEnabled } from '../../../hooks/usePasskeyEnabled';
import { useEvent } from '../../../utils/analytics/useEvent';
import { useAuthContext } from '../../../utils/auth/AuthProvider';
import { signInWithPasskey } from '../../../utils/auth/passkeySignIn';
import { IDENTITY_WIDGET_PATHS } from '../../../utils/constants';
import { UserAccountContext, useUserAccount } from '../../../utils/userAccountContext';
import { Container } from '../Container';
import { PageLayout } from '../PageLayout';

import {
  StyledSMSIcon,
  StyledHeading,
  StyledDescription,
  StyledButton,
  StyledResendText,
  StyledAlertContainer,
  StyledSecondaryButton,
} from './styles';

export interface SMSVerificationProps {
  phoneNumber?: string;
  verificationCode: string;
  onVerificationCodeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onVerificationCodeBlur?: () => void;
  onSubmit: () => void;
  onResend: () => void;
  onCloseAlert: () => void;
  isLoading: boolean;
  inputError?: boolean;
  inputHint?: string;
  isCountdownRunning: boolean;
  secondsLeft: number;
  submitButtonLabel?: string;
  codeResentSuccessfully?: boolean;
  errorMessage?: string;
  openAlert?: boolean;
  type?: 'enrollment' | 'authentication';
}

export function SMSVerification({
  phoneNumber,
  verificationCode,
  onVerificationCodeChange,
  onVerificationCodeBlur,
  onSubmit,
  onResend,
  onCloseAlert,
  isLoading,
  inputError = false,
  inputHint = '',
  isCountdownRunning,
  secondsLeft,
  submitButtonLabel,
  codeResentSuccessfully,
  errorMessage,
  openAlert = true,
  type,
}: SMSVerificationProps) {
  const { authClient } = useAuthContext();
  const { hasEmailEnrolled, hasPasskeyEnrolled, email: userEmail, mgmId } = useContext(UserAccountContext);
  const { email } = useUserAccount();
  const { getContentByPath } = useContent();
  const redirectToApp = useAppRedirect();
  const trackEvent = useEvent();
  const { passkeyEnabled } = usePasskeyEnabled();

  // State for passkey signin functionality
  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false);

  // Common alert state
  const [alert, setAlert] = useState<{
    message: string;
    variant: 'success' | 'error';
    testId: string;
  } | null>(null);
  const handleSubmit = () => {
    setAlert(null);
    onCloseAlert();
    onSubmit();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && verificationCode.length === 6 && !isLoading) {
      handleSubmit();
    }
  };

  const renderAlerts = () => {
    // Use common alert system - prioritize custom alert, then existing props
    if (alert) {
      return (
        <Alert
          open={openAlert}
          onClose={() => setAlert(null)}
          variant={alert.variant}
          message={alert.message}
          data-testid={alert.testId}
          role='alert'
        />
      );
    }

    if (codeResentSuccessfully) {
      return (
        <Alert
          open={openAlert}
          onClose={onCloseAlert}
          variant='success'
          message={getContentByPath('mfa.sms.codeResentSuccess')}
          data-testid='code-sent-success'
          role='alert'
        />
      );
    }

    if (errorMessage) {
      return (
        <Alert
          open={openAlert}
          onClose={onCloseAlert}
          variant='error'
          message={errorMessage}
          data-testid='code-resent-error'
          role='alert'
        />
      );
    }

    return null;
  };
  const goToEmailVerification = async () => {
    await trackEvent('identity – unified MFA CTAs', 'Send code to your email', 'unifiedmfactas', {}, true);
    const emailAuthHandler = authClient.idx.stepHandlers[IdxStep.AuthEmail];
    await emailAuthHandler.selectAuth(AuthenticatorKey.OKTA_EMAIL);
    navigate(IDENTITY_WIDGET_PATHS.MfaAuthEmail, true);
  };

  const handlePasskeySignIn = async () => {
    try {
      setIsPasskeyLoading(true);
      setAlert(null); // Clear any existing alerts

      // Check if passkey feature flag is enabled
      if (!passkeyEnabled) {
        return;
      }

      if (!email) {
        setAlert({
          message: 'Email is required for passkey authentication.',
          variant: 'error',
          testId: 'passkey-signin-error',
        });
        return;
      }

      const result = await signInWithPasskey({
        email: userEmail,
        authClient,
        trackEvent,
        redirectToApp,
        mgmId: mgmId,
      });

      if (!result.success && result.error) {
        onCloseAlert();
        setAlert({
          message: result.error,
          variant: 'error',
          testId: 'passkey-signin-error',
        });
      }
      // If successful, redirectToApp() will be called from the utility
    } catch (error) {
      console.error('Passkey signin error in SMSVerification:', error);
      onCloseAlert();
      setAlert({
        message: 'An unexpected error occurred during passkey signin.',
        variant: 'error',
        testId: 'passkey-signin-error',
      });
    } finally {
      setIsPasskeyLoading(false);
    }
  };

  const goToContactSupport = async () => {
    await trackEvent('identity – unified MFA CTAs', "I can't access my account", 'unifiedmfactas', {}, true);
    window.history.replaceState(null, '', '/identity');
    navigate(IDENTITY_WIDGET_PATHS.MfaContactSupport);
  };

  return (
    <div>
      <PageLayout
        testId='sms-code-entered'
        title={getContentByPath('mfa.sms.title')}
        description={getContentByPath('mfa.sms.description')}
      >
        <Container height='auto' width='26%'>
          <StyledAlertContainer>{renderAlerts()}</StyledAlertContainer>
          <StyledSMSIcon>
            <Icon name='mobile-code' variant='outlined' size='large' aria-hidden='true' />
          </StyledSMSIcon>

          <StyledHeading variant='heading-medium-1' as='h1' testId='page-title'>
            {getContentByPath('mfa.sms.title')}
          </StyledHeading>

          <StyledDescription variant='body-regular-s'>
            {getContentByPath('mfa.sms.body')}
            {phoneNumber}
          </StyledDescription>

          <TextInput
            variant='default'
            label={getContentByPath('mfa.sms.fields.verificationCode.title')}
            id='verificationCode'
            testId='verification-code'
            disabled={isLoading}
            value={verificationCode}
            onChange={onVerificationCodeChange}
            onBlur={onVerificationCodeBlur}
            onKeyDown={handleKeyDown}
            maxLength={6}
            autoComplete='one-time-code'
            type='text'
            inputMode='numeric'
            error={inputError}
            hint={inputHint}
          />

          <StyledButton
            onClick={handleSubmit}
            testId='submit-code'
            type='submit'
            loading={isLoading}
            disabled={isLoading || verificationCode?.length !== 6 || inputError}
            shape='square'
            size='large'
            variant='primary'
            label={submitButtonLabel}
            fullWidth
          />
          {(codeResentSuccessfully || errorMessage || inputError) && (
            <StyledResendText variant='body-regular-m'>
              {isCountdownRunning ? (
                `Resend code available in ${secondsLeft} ${secondsLeft === 1 ? 'second' : 'seconds'}`
              ) : (
                <TextLink button type='button' variant='small' onClick={onResend}>
                  {getContentByPath('mfa.sms.resendLink')}
                </TextLink>
              )}
            </StyledResendText>
          )}
          {type === 'authentication' && (
            <>
              {hasEmailEnrolled && (
                <StyledSecondaryButton
                  onClick={goToEmailVerification}
                  testId='email-code'
                  type='button'
                  loading={isLoading}
                  disabled={isLoading || isPasskeyLoading}
                  shape='square'
                  size='large'
                  variant='secondary'
                  label={getContentByPath('mfa.sms.emailCTA')}
                  fullWidth
                />
              )}
              {passkeyEnabled && hasPasskeyEnrolled && (
                <StyledSecondaryButton
                  onClick={handlePasskeySignIn}
                  testId='passkey-code'
                  type='button'
                  loading={isPasskeyLoading}
                  disabled={isLoading || isPasskeyLoading}
                  shape='square'
                  size='large'
                  variant='secondary'
                  label={getContentByPath('mfa.sms.passkeyCTA')}
                  fullWidth
                />
              )}

              <StyledResendText variant='body-regular-m'>
                <TextLink button type='button' variant='large' onClick={goToContactSupport}>
                  {getContentByPath('mfa.sms.cantAccessAccountLink')}
                </TextLink>
              </StyledResendText>
            </>
          )}
        </Container>
      </PageLayout>
    </div>
  );
}

SMSVerification.displayName = 'SMSVerification';
